import _ from 'lodash';

/**
 * This file maintains the running list of A/B experiments
 * running on the landing page client that react is aware of.
 */
export const CONTROL = 'control';

export const Experiment = {
  MARKETPLACE_CAP_ONE: {
    id: 'cc_cap_one_april_2019',
    variant: {
      CONTROL,
      ALTERNATE_OFFER_1: 'alternate_offer_1',
      ALTERNATE_OFFER_2: 'alternate_offer_2',
    },
    slugs: [
      // List the landing page slug strings this experiment should be active on
      'cashback',
      'rewards',
      'mcashback',
      'mrewards',
    ],
    substitutions: [
      // Capital One® VentureOne® Rewards Credit Card
      {
        controlCardId: 11713,
        variant1CardId: 65577,
        variant2CardId: 65578,
      },

      // Capital One® Venture® Rewards Credit Card
      {
        controlCardId: 11712,
        variant1CardId: 65579,
        variant2CardId: 65580,
      },

      // Capital One® Quicksilver® Cash Rewards Credit Card
      {
        controlCardId: 11718,
        variant1CardId: 65581,
        variant2CardId: 65582,
      },

      // Capital One® Savor® Cash Rewards Credit Card
      {
        controlCardId: 65455,
        variant1CardId: 65583,
        variant2CardId: 65584,
      },

      // Capital One® SavorOne℠ Cash Rewards Credit Card
      {
        controlCardId: 65543,
        variant1CardId: 65585,
        variant2CardId: 65586,
      },
    ],
  },
  MARKETPLACE_MOBILE_MOVE_CTA: {
    id: 'marketplace_mobile_move_cta',
    variant: {
      CONTROL,
      ON: 'on',
    },
  },
};

// If you use this function, make sure you are also outputting the `ABContainer`
// somewhere for your test
export const makeVariantPath = ({ id }) =>
  `availableExperiments.${id}.variantName`;

/**
 * For use in `isExperimentActive`.
 * @param {Object} experiment - the experiment.
 * @returns {string[]} - the list of non-control variants for the given experiment.
 */
export const getNonControlVariants = (experiment) =>
  _.reject(experiment.variant, (variantName) => variantName === CONTROL) || [];

/**
 * Generic function to fetch the active variant of a particular experiment
 *
 * The established pattern is to return CONTROL even if the experiment object is malformed or the
 * experiment doesn't exist, so that at least something is shown on the page.
 *
 * @param {object} experiment - the experiment for which the active variant is desired
 * @param {Object} experiments - the map of experiments from the redux store to check against
 * @returns {string} - the name of the active variant or 'control' as default
 */
export const getActiveVariant = (experiment = {}, experiments = {}) =>
  _.get(experiments, makeVariantPath(experiment), CONTROL);

/**
 * Generic function to determine if an experiment is active. An experiment is active if the
 * currently active variant of the test is one of the variants defined in experiment definition
 * (in Experiment object) and is not CONTROL (thus the idea of non-Control variants).
 *
 * @param {Object} experiment - the experiment in question
 * @param {Object} experiments - the map of experiments from the redux store to check against
 * @returns {boolean} - whether the active variant is not control
 */
export const isExperimentActive = (experiment, experiments) =>
  getNonControlVariants(experiment).indexOf(
    getActiveVariant(experiment, experiments)
  ) !== -1;

/**
 * Generic function to determine if an experiment is available
 *
 * @param experimentName - the name of the experiment you want to see is active
 * @param experiments - the list of experiments from the redux store to check against
 *
 * @returns Boolean - whether the test exists and is running (control or variant)
 */
export const isExperimentAvailable = (experimentName, experiments) =>
  _.has(experiments, `availableExperiments.${experimentName}`);

export const isControlAndOneVariantCardIDPresent = ({
  cardIDs,
  controlCardId,
  variant1CardId,
  variant2CardId,
}) =>
  Array.isArray(cardIDs) &&
  cardIDs.length > 0 &&
  cardIDs.indexOf(controlCardId) >= 0 &&
  (cardIDs.indexOf(variant1CardId) >= 0 ||
    cardIDs.indexOf(variant2CardId) >= 0);

export const processSubstitution = ({
  activeVariant,
  variantAlternateOffer1,
  variantAlternateOffer2,
  cardIDs,
  substitution,
}) => {
  // reference to the substitution ids.
  const controlCardId = _.get(substitution, 'controlCardId', 0);
  const variant1CardId = _.get(substitution, 'variant1CardId', 0);
  const variant2CardId = _.get(substitution, 'variant2CardId', 0);

  // Check if control and either variant card is currently present - if not, we make no substitutions.
  if (
    isControlAndOneVariantCardIDPresent({
      cardIDs,
      controlCardId,
      variant1CardId,
      variant2CardId,
    })
  ) {
    // At least one of the variants is present.  Determine which id from the group should result
    switch (activeVariant) {
      case variantAlternateOffer1:
        // Remove variant 2 and control ids
        _.pull(cardIDs, controlCardId, variant2CardId);
        break;
      case variantAlternateOffer2:
        // Remove variant 1 and control ids
        _.pull(cardIDs, controlCardId, variant1CardId);
        break;
      default:
        // Control - Remove BOTH variant ids
        _.pull(cardIDs, variant1CardId, variant2CardId);
        break;
    }
  }
};

/**
 * Marketplace-specific function to remove a card from a collection based on experiment and category
 *
 * @param {Object} experiments - the list of experiments from the redux store to check against
 * @param {Object} experiment - the experiment in question
 * @param {String} category - category name to match
 * @param {Object} cardIDs - Array of card ids that we should remove values from
 *
 * @returns Boolean - whether the test exists and is running (control or variant)
 */
export const removeControlOrVariantCardByCategory = ({
  experiment,
  experiments,
  category,
  cardIDs,
}) => {
  // First check the experiment is active on this category/slug
  if (_.get(experiment, 'slugs', []).indexOf(category) >= 0) {
    const activeVariant = getActiveVariant(experiment, experiments);
    const variantAlternateOffer1 = experiment.variant.ALTERNATE_OFFER_1;
    const variantAlternateOffer2 = experiment.variant.ALTERNATE_OFFER_2;

    // Loop over each substitution group and process if each is valid
    _.each(_.get(experiment, 'substitutions', []), (substitution) =>
      processSubstitution({
        activeVariant,
        variantAlternateOffer1,
        variantAlternateOffer2,
        cardIDs,
        substitution,
      })
    );
  }
};
